import { Link } from 'react-router-dom'
import {
  Activity, AlertTriangle, CalendarClock, ListChecks, ServerCog,
  KeyRound, ShieldAlert, ShieldCheck, Lock, FileX, Siren, FileCheck2, Building2,
  MessageSquareText, ArrowUpNarrowWide, Megaphone, Flag, Boxes, Database, ScrollText,
} from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'

/**
 * Operations Hub — function-grouped entry point to the lane-A platform subsystems
 * (distinct from the TZ-module map at /modules). Pure navigation; no backend.
 */

interface HubLink {
  to: string
  label: string
  icon: React.ReactNode
  dash?: boolean
}
interface HubGroup {
  title: string
  links: HubLink[]
}

const GROUPS: HubGroup[] = [
  {
    title: 'Надёжность / Операции',
    links: [
      { to: '/reliability', label: 'SLO / надёжность', icon: <Activity size={15} />, dash: true },
      { to: '/incidents', label: 'Инциденты', icon: <AlertTriangle size={15} /> },
      { to: '/oncall', label: 'Дежурства', icon: <CalendarClock size={15} /> },
      { to: '/status-page', label: 'Страница статуса', icon: <ServerCog size={15} /> },
      { to: '/maintenance-windows', label: 'Техработы', icon: <CalendarClock size={15} /> },
      { to: '/job-runs', label: 'Фоновые задачи', icon: <ListChecks size={15} /> },
    ],
  },
  {
    title: 'Безопасность',
    links: [
      { to: '/security-posture', label: 'Безопасность / риск', icon: <ShieldCheck size={15} />, dash: true },
      { to: '/jit/access', label: 'JIT-доступ', icon: <KeyRound size={15} /> },
      { to: '/jit/break-glass', label: 'Break-glass', icon: <ShieldAlert size={15} /> },
    ],
  },
  {
    title: 'Соответствие (Compliance)',
    links: [
      { to: '/compliance', label: 'Compliance-статус', icon: <ShieldCheck size={15} />, dash: true },
      { to: '/gdpr-requests', label: 'GDPR / DSAR', icon: <ShieldCheck size={15} /> },
      { to: '/legal-holds', label: 'Legal holds', icon: <Lock size={15} /> },
      { to: '/takedowns', label: 'Удаление контента', icon: <FileX size={15} /> },
      { to: '/emergency-requests', label: 'Экстренные запросы', icon: <Siren size={15} /> },
      { to: '/legal-docs', label: 'Юр. документы', icon: <ScrollText size={15} /> },
      { to: '/data-processors', label: 'Обработчики данных', icon: <Building2 size={15} /> },
    ],
  },
  {
    title: 'Поддержка',
    links: [
      { to: '/canned-responses', label: 'Шаблоны ответов', icon: <MessageSquareText size={15} /> },
      { to: '/escalation-rules', label: 'Правила эскалации', icon: <ArrowUpNarrowWide size={15} /> },
    ],
  },
  {
    title: 'Коммуникации / Модерация',
    links: [
      { to: '/announcements', label: 'Объявления / баннеры', icon: <Megaphone size={15} /> },
      { to: '/automod/flags', label: 'Флаги контента', icon: <Flag size={15} /> },
    ],
  },
  {
    title: 'Платформа',
    links: [
      { to: '/platform/modules', label: 'Модули', icon: <Boxes size={15} /> },
      { to: '/platform/db', label: 'Модели (БД)', icon: <Database size={15} /> },
      { to: '/legal-docs', label: 'Согласия', icon: <FileCheck2 size={15} /> },
    ],
  },
]

export function OpsHubScreen() {
  return (
    <>
      <PageHeader title="Операции" screenId="A2" description="Единая точка входа к подсистемам платформы (по функциям)" />
      <div className="min-h-0 flex-1 overflow-auto p-4">
        <div className="mx-auto max-w-4xl space-y-6">
          {GROUPS.map((g) => (
            <section key={g.title}>
              <h2 className="mb-2 text-xs font-medium uppercase tracking-wide text-tertiary">{g.title}</h2>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {g.links.map((l) => (
                  <Link
                    key={l.to + l.label}
                    to={l.to}
                    className={`flex items-center gap-2 rounded-lg border px-3 py-2.5 text-sm transition-colors hover:border-accent ${l.dash ? 'border-accent/40 bg-accent/5 text-primary' : 'border-border-default bg-surface-2 text-secondary hover:text-primary'}`}
                  >
                    <span className="text-tertiary">{l.icon}</span>
                    <span className="min-w-0 truncate">{l.label}</span>
                    {l.dash && <span className="ml-auto text-[10px] uppercase text-accent">dash</span>}
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>
      </div>
    </>
  )
}
